import { useEffect, useMemo, useRef, useState } from 'react'

import { ProductBrand } from '@/app/ProductBrand'
import { type PingResult, type StartResult } from '@/platform/access/learner-session'
import { PIN_MAX_LENGTH, PIN_MIN_LENGTH, type PinResult } from '@/platform/family/api'
import { type LearningService } from '@/platform/learning/service'
import {
  type MaterialLearner,
  type MaterialLearningApi,
  StartLearningError,
} from '@/platform/materials/learning'
import {
  bindReader,
  ExtractionError,
  type MaterialExtractionService,
  type MaterialReader,
} from '@/platform/materials/extraction'
import { type ReadAloudApi, type ReadAloudLearner } from '@/platform/read-aloud/api'
import { toAppError } from '@/shared/errors/envelope'
import { PinPad } from '@/shared/ui/PinPad'

import { LearnerPinScreen, type SessionStarted } from './entry/LearnerPinScreen'
import { ChildLearning } from './learning/ChildLearning'
import { type DemoRunReport, type LoadSampleMaterials } from './materials/model'

/**
 * FR-03 / SEC-C child mode, inside a learner session (D-16): the app is
 * locked to the learner whose PIN started the session. Leaving to Parent Home
 * needs the parent PIN, verified server-side with a five-minute lock after
 * five wrong attempts; "Đổi người học" ends the session and goes back to
 * learner selection. Inside, the learner's Child Home and lessons; they stay
 * mounted while a PIN pad is open, so the learner returns to the same point.
 *
 * The session is watched: every `pingIntervalMs` (and when the app comes back
 * to the foreground) the server is asked whether it is still live, reporting
 * whether the learner did anything since (only activity extends the idle
 * time). When it has ended, the learner's PIN is asked again over the lesson
 * (screen F). Learner-scoped requests (school material upload) carry the
 * session token; a refusal for an invalid session also asks for the PIN.
 */

export interface ChildModeScreenProps {
  readonly learnerId: string
  readonly nickname: string
  readonly learning: LearningService
  /** DEMO school materials, local/development only (D-02). */
  readonly demoMaterials?: LoadSampleMaterials | null
  /** Real school material reading (Slice 3), bound here to this learner. */
  readonly materials?: MaterialExtractionService | null
  readonly onDemoRun?: (report: DemoRunReport) => void
  readonly initialLockedUntil?: string | null
  readonly onVerifyPin: (pin: string) => Promise<PinResult>
  /** Parent PIN verified: leave child mode (the app ends the learner session). */
  readonly onExit: () => void
  /** The current learner session token (D-16). */
  readonly sessionToken: string
  /** School-material lessons (D-17), bound here to the learner session. */
  readonly materialLearning?: MaterialLearningApi | null
  /** Read Aloud (KT1 integrated), bound here to the learner session. */
  readonly readAloud?: ReadAloudApi | null
  readonly onPingSession: (token: string, active: boolean) => Promise<PingResult>
  /** Starts a new session for this learner after it expired. */
  readonly onRestartSession: (pin: string) => Promise<StartResult>
  readonly onSessionRenewed: (session: SessionStarted) => void
  /** End this learner's session and go back to learner selection. */
  readonly onSwitchLearner: () => void
  readonly pingIntervalMs?: number
  readonly now?: () => number
}

export const SESSION_PING_MS = 60_000

function remaining(lockedUntil: string, now: number): number {
  return Math.max(0, Math.ceil((Date.parse(lockedUntil) - now) / 1000))
}

function formatSeconds(total: number): string {
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${String(m)}:${String(s).padStart(2, '0')}`
}

export function ChildModeScreen({
  learnerId,
  nickname,
  learning,
  demoMaterials = null,
  materials = null,
  onDemoRun,
  initialLockedUntil = null,
  onVerifyPin,
  onExit,
  sessionToken,
  materialLearning = null,
  readAloud = null,
  onPingSession,
  onRestartSession,
  onSessionRenewed,
  onSwitchLearner,
  pingIntervalMs = SESSION_PING_MS,
  now = Date.now,
}: ChildModeScreenProps): React.JSX.Element {
  const [askingPin, setAskingPin] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [lockedUntil, setLockedUntil] = useState<string | null>(initialLockedUntil)
  const [, setTick] = useState(0)
  const parentButton = useRef<HTMLButtonElement>(null)
  const [expired, setExpired] = useState(false)

  // Learner activity since the last ping: only this extends the idle time.
  const active = useRef(false)
  useEffect(() => {
    const mark = () => {
      active.current = true
    }
    document.addEventListener('pointerdown', mark)
    document.addEventListener('keydown', mark)
    return () => {
      document.removeEventListener('pointerdown', mark)
      document.removeEventListener('keydown', mark)
    }
  }, [])

  // The latest callback, so a re-rendered parent does not restart the timer.
  const ping = useRef(onPingSession)
  useEffect(() => {
    ping.current = onPingSession
  }, [onPingSession])

  useEffect(() => {
    if (expired) return
    let cancelled = false
    const check = () => {
      const wasActive = active.current
      active.current = false
      ping.current(sessionToken, wasActive).then(
        (r) => {
          if (!cancelled && !r.valid) setExpired(true)
        },
        // A failed check (offline) is not an expiry; the next one decides.
        () => undefined,
      )
    }
    const onVisible = () => {
      if (document.visibilityState === 'visible') check()
    }
    const id = setInterval(check, pingIntervalMs)
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      cancelled = true
      clearInterval(id)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [sessionToken, pingIntervalMs, expired])

  const materialReader = useMemo((): MaterialReader | null => {
    if (materials === null) return null
    const reader = bindReader(materials, sessionToken)
    const watch = <T,>(p: Promise<T>): Promise<T> =>
      p.catch((e: unknown) => {
        if (e instanceof ExtractionError && e.code === 'learner_session_invalid')
          setExpired(true)
        throw e
      })
    return {
      availability: async () => {
        const a = await reader.availability()
        if (!a.available && a.why === 'learner_session_invalid') setExpired(true)
        return a
      },
      extract: (request) => watch(reader.extract(request)),
    }
  }, [materials, sessionToken])

  // Lessons use the same session; an invalid session asks the PIN again.
  const materialLearner = useMemo((): MaterialLearner | null => {
    if (materialLearning === null) return null
    const l = materialLearning.forSession(sessionToken)
    const watch = <T,>(p: Promise<T>): Promise<T> =>
      p.catch((e: unknown) => {
        const code = e instanceof StartLearningError ? e.code : toAppError(e).code
        if (code === 'learner_session_invalid' || code === 'LEARNER_SESSION_INVALID')
          setExpired(true)
        throw e
      })
    return {
      start: (input) => watch(l.start(input)),
      getPractice: (id) => watch(l.getPractice(id)),
      listMine: () => watch(l.listMine()),
      openHelp: (id, mode) => watch(l.openHelp(id, mode)),
      submit: (id, answer, client) => watch(l.submit(id, answer, client)),
      complete: (id) => watch(l.complete(id)),
    }
  }, [materialLearning, sessionToken])

  // Read Aloud uses the same session; an invalid session asks the PIN again.
  const readAloudLearner = useMemo((): ReadAloudLearner | null => {
    if (readAloud === null) return null
    const r = readAloud.forSession(sessionToken)
    const watch = <T,>(p: Promise<T>): Promise<T> =>
      p.catch((e: unknown) => {
        if (toAppError(e).code === 'LEARNER_SESSION_INVALID') setExpired(true)
        throw e
      })
    return {
      status: async () => {
        const s = await watch(r.status())
        if (!s.learner_session) setExpired(true)
        return s
      },
      history: () => watch(r.history()),
      submit: async (input) => {
        const out = await r.submit(input)
        if (out.kind === 'refused' && out.why === 'learner_session_invalid')
          setExpired(true)
        return out
      },
    }
  }, [readAloud, sessionToken])

  // Closing the PIN pad returns focus to the button that opened it.
  const closePin = () => {
    setAskingPin(false)
    setError(null)
    parentButton.current?.focus()
  }

  const lockSeconds = lockedUntil === null ? 0 : remaining(lockedUntil, now())

  useEffect(() => {
    if (lockedUntil === null) return
    const id = setInterval(() => {
      if (remaining(lockedUntil, now()) === 0) setLockedUntil(null)
      setTick((t) => t + 1)
    }, 1000)
    return () => {
      clearInterval(id)
    }
  }, [lockedUntil, now])

  const verify = async (pin: string) => {
    setBusy(true)
    setError(null)
    try {
      const result = await onVerifyPin(pin)
      if (result.verified) {
        onExit()
        return
      }
      if (result.locked_until !== null) {
        setLockedUntil(result.locked_until)
      } else {
        setError(`PIN chưa đúng. Còn ${String(result.attempts_remaining)} lần thử.`)
      }
    } catch (e) {
      setError(
        toAppError(e).code === 'SESSION_EXPIRED'
          ? 'Phiên đăng nhập đã hết hạn.'
          : 'Không kiểm tra được PIN. Vui lòng thử lại.',
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="flex min-h-dvh flex-col bg-amber-50 dark:bg-slate-950">
      <div className="flex items-center justify-between gap-3 p-3">
        <ProductBrand className="px-2 text-slate-700 dark:text-slate-300" />
        <div className="flex items-center">
          <button
            type="button"
            className="min-h-11 rounded-full px-3 text-base text-slate-600 dark:text-slate-400"
            onClick={onSwitchLearner}
          >
            Đổi người học
          </button>
          <button
            ref={parentButton}
            type="button"
            className="min-h-11 rounded-full px-3 text-base text-slate-600 dark:text-slate-400"
            onClick={() => {
              setAskingPin(true)
            }}
          >
            Phụ huynh
          </button>
        </div>
      </div>

      {askingPin && (
        <div className="flex flex-1 flex-col justify-center px-6 pb-10">
          {lockSeconds > 0 ? (
            <div className="mx-auto max-w-xs text-center" role="alert">
              <p className="text-4xl" aria-hidden>
                ⏳
              </p>
              <p className="mt-3 text-lg font-semibold">Đã nhập sai PIN 5 lần</p>
              <p className="mt-1 text-slate-700 dark:text-slate-300">
                Thử lại sau {formatSeconds(lockSeconds)}.
              </p>
              <button
                type="button"
                className="mt-5 px-4 text-slate-600 underline dark:text-slate-400"
                onClick={closePin}
              >
                Quay lại
              </button>
            </div>
          ) : (
            <PinPad
              title="Nhập PIN phụ huynh"
              error={error}
              minLength={PIN_MIN_LENGTH}
              maxLength={PIN_MAX_LENGTH}
              busy={busy}
              onSubmit={(pin) => void verify(pin)}
              onCancel={closePin}
            />
          )}
        </div>
      )}
      {expired && !askingPin && (
        <LearnerPinScreen
          learner={{ id: learnerId, nickname }}
          expired
          backLabel="Đổi người học"
          onStart={onRestartSession}
          onStarted={(session) => {
            setExpired(false)
            onSessionRenewed(session)
          }}
          onBack={onSwitchLearner}
          now={now}
        />
      )}
      <div className="flex flex-1 flex-col" hidden={askingPin || expired}>
        <ChildLearning
          learning={learning}
          learnerId={learnerId}
          nickname={nickname}
          demoMaterials={demoMaterials}
          materialReader={materialReader}
          materialLearner={materialLearner}
          readAloud={readAloudLearner}
          {...(onDemoRun === undefined ? {} : { onDemoRun })}
        />
      </div>
    </main>
  )
}
